import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPayPeriodConfig, getCurrentPayPeriod, formatPayDate } from '../../hooks/payPeriodUtils.js';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { IcoMapPin, IcoCalendar, IcoClock } from './DashIcons';

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatElapsed(ms) {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getAuthHeaders() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` };
}

function getUser() {
  return JSON.parse(localStorage.getItem('user') || '{}');
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: '#fef3c7', color: '#92400e', border: '#fcd34d', label: 'Pending' },
    approved: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', label: 'Approved' },
    rejected: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', label: 'Rejected' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      padding: '0.2rem 0.65rem', borderRadius: '999px',
      fontSize: '0.7rem', fontWeight: '700', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ─── Clock Widget ─────────────────────────────────────────────────────────────

function ClockWidget({ onClockOut }) {
  const [status, setStatus]         = useState('loading');
  const [clockInTime, setClockInTime] = useState(null);
  const [elapsed, setElapsed]       = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [actionMsg, setActionMsg]   = useState('');
  const [error, setError]           = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    fetch('/api/clock/status', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.session) {
          setStatus('in');
          setClockInTime(new Date(data.session.clockIn));
        } else {
          setStatus('out');
        }
      })
      .catch(() => setStatus('out'));
  }, []);

  useEffect(() => {
    if (status === 'in' && clockInTime) {
      timerRef.current = setInterval(() => setElapsed(Date.now() - clockInTime.getTime()), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [status, clockInTime]);

  const handleClockIn = async () => {
    setError('');
    try {
      const res = await fetch('/api/clock/clock-in', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Clock-in failed'); return; }
      setClockInTime(new Date(data.session.clockIn));
      setStatus('in');
      setElapsed(0);
      setActionMsg('Clocked in');
      setTimeout(() => setActionMsg(''), 4000);
    } catch { setError('Clock-in failed. Try again.'); }
  };

  const handleClockOut = async () => {
    setConfirming(false);
    setError('');
    try {
      const res = await fetch('/api/clock/clock-out', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Clock-out failed'); return; }
      clearInterval(timerRef.current);
      setStatus('out');
      setClockInTime(null);
      setElapsed(0);
      const hours = data.entry?.hoursWorked?.toFixed(2);
      setActionMsg(`${hours}h submitted for review`);
      setTimeout(() => setActionMsg(''), 6000);
      if (onClockOut) onClockOut();
    } catch { setError('Clock-out failed. Try again.'); }
  };

  const clockedIn = status === 'in';

  return (
    <div style={{
      background: clockedIn
        ? 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)'
        : 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
      borderRadius: '20px',
      padding: '2rem',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.5s ease',
      boxShadow: clockedIn
        ? '0 8px 32px rgba(6,78,59,0.4)'
        : '0 8px 32px rgba(67,56,202,0.4)',
    }}>
      {/* Decorative circle */}
      <div style={{
        position: 'absolute', top: '-40px', right: '-40px',
        width: '160px', height: '160px', borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-60px', left: '30%',
        width: '200px', height: '200px', borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
        <div>
          {/* Pulse indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: clockedIn ? '#34d399' : 'rgba(255,255,255,0.4)',
              display: 'inline-block',
              boxShadow: clockedIn ? '0 0 0 4px rgba(52,211,153,0.3)' : 'none',
              animation: clockedIn ? 'pulse 2s infinite' : 'none',
            }} />
            <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.85 }}>
              {status === 'loading' ? 'Checking…' : clockedIn ? 'On the Clock' : 'Not Clocked In'}
            </span>
          </div>

          {clockedIn ? (
            <>
              <div style={{
                fontSize: '3rem', fontWeight: '800', letterSpacing: '0.04em',
                fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                fontFamily: "'Courier New', monospace",
              }}>
                {formatElapsed(elapsed)}
              </div>
              <div style={{ fontSize: '0.82rem', opacity: 0.75, marginTop: '0.5rem' }}>
                Since {clockInTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ·{' '}
                {clockInTime?.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
            </>
          ) : (
            <div style={{ fontSize: '1.1rem', opacity: 0.75, marginTop: '0.25rem' }}>
              Tap Clock In to start your shift
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
          {status !== 'loading' && !clockedIn && (
            <button onClick={handleClockIn} style={{
              padding: '0.875rem 1.75rem',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              color: 'white', border: '1.5px solid rgba(255,255,255,0.3)',
              borderRadius: '12px', fontWeight: '700', fontSize: '1rem',
              cursor: 'pointer', transition: 'all 0.2s',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            }}
              onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.15)'}
            >
              ▶ Clock In
            </button>
          )}
          {clockedIn && !confirming && (
            <button onClick={() => setConfirming(true)} style={{
              padding: '0.875rem 1.75rem',
              background: 'rgba(239,68,68,0.2)',
              backdropFilter: 'blur(8px)',
              color: 'white', border: '1.5px solid rgba(239,68,68,0.5)',
              borderRadius: '12px', fontWeight: '700', fontSize: '1rem',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.target.style.background = 'rgba(239,68,68,0.35)'}
              onMouseLeave={e => e.target.style.background = 'rgba(239,68,68,0.2)'}
            >
              ⏹ Clock Out
            </button>
          )}
          {confirming && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', opacity: 0.85, fontWeight: '600' }}>Submit shift for review?</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setConfirming(false)} style={{ padding: '0.5rem 1rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleClockOut} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}>Yes, Clock Out</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(actionMsg || error) && (
        <div style={{
          marginTop: '1.25rem', padding: '0.6rem 0.875rem',
          background: error ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.12)',
          border: `1px solid ${error ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.2)'}`,
          borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
          position: 'relative', zIndex: 1,
        }}>
          {error || actionMsg}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(52,211,153,0.3); }
          50% { box-shadow: 0 0 0 8px rgba(52,211,153,0.1); }
        }
      `}</style>
    </div>
  );
}

// ─── Recent Entries ───────────────────────────────────────────────────────────

function RecentEntries({ entries, loading }) {
  if (loading) return <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Loading entries…</p>;
  if (!entries.length) return <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No entries yet</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {entries.slice(0, 6).map(entry => (
        <div key={entry._id} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.875rem 1rem',
          background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
        }}>
          <div>
            <div style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem' }}>
              {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
              {entry.hoursWorked?.toFixed(2)}h
              {entry.reg > 0 && ` · REG ${entry.reg}h`}
              {entry.ot1 > 0 && ` · OT1 ${entry.ot1}h`}
              {entry.mileage > 0 && ` · ${entry.mileage}mi`}
            </div>
            {entry.status === 'rejected' && entry.reviewNotes && (
              <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '0.2rem' }}>
                Rejected: {entry.reviewNotes}
              </div>
            )}
          </div>
          <StatusBadge status={entry.status || 'pending'} />
        </div>
      ))}
    </div>
  );
}

// ─── Schedule Widget ──────────────────────────────────────────────────────────

function UpcomingSchedule({ schedule, loading }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();

  // Build 7-day view
  const week = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    const shift = schedule?.find(s => s.date?.startsWith(dateStr));
    return { date: d, dateStr, shift };
  });

  if (loading) return <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Loading schedule…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {week.map(({ date, shift }, i) => {
        const isToday = i === 0;
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '0.875rem',
            padding: '0.75rem 1rem',
            background: isToday
              ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
              : shift ? '#f9fafb' : 'transparent',
            borderRadius: '10px',
            border: isToday ? '1px solid #c4b5fd' : shift ? '1px solid #e5e7eb' : '1px solid transparent',
          }}>
            {/* Day label */}
            <div style={{ width: '36px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: '700', color: isToday ? '#7c3aed' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{days[date.getDay()]}</div>
              <div style={{ fontSize: '1rem', fontWeight: '800', color: isToday ? '#5b21b6' : '#374151' }}>{date.getDate()}</div>
            </div>

            {/* Shift info */}
            {shift ? (
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '0.875rem', color: isToday ? '#5b21b6' : '#111827' }}>
                  {shift.startTime} — {shift.endTime}
                </div>
                {shift.location && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.1rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><IcoMapPin size={12} color="#6b7280"/> {shift.location}</div>}
                {shift.notes && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.1rem', fontStyle: 'italic' }}>{shift.notes}</div>}
              </div>
            ) : (
              <div style={{ flex: 1, fontSize: '0.825rem', color: '#d1d5db', fontStyle: 'italic' }}>No shift scheduled</div>
            )}

            {isToday && shift && (
              <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#7c3aed', background: '#ede9fe', border: '1px solid #c4b5fd', padding: '0.15rem 0.5rem', borderRadius: '999px' }}>TODAY</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Pay Period Card ──────────────────────────────────────────────────────────

function PayPeriodCard({ payPeriod, entries }) {
  if (!payPeriod) return null;

  const start = new Date(payPeriod.start);
  const end   = new Date(payPeriod.end);
  const today = new Date();
  const totalDays = Math.ceil((end - start) / 86400000);
  const elapsed   = Math.min(Math.ceil((today - start) / 86400000), totalDays);
  const pct = Math.round((elapsed / totalDays) * 100);

  // Hours this period
  const periodEntries = entries.filter(e => {
    const d = new Date(e.date);
    return d >= start && d <= end;
  });
  const totalHours = periodEntries.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
  const approvedHours = periodEntries.filter(e => e.status === 'approved').reduce((sum, e) => sum + (e.hoursWorked || 0), 0);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #faf5ff 0%, #f0f4ff 100%)',
      border: '1px solid #e0e7ff',
      borderRadius: '14px',
      padding: '1.25rem 1.5rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Current Pay Period</div>
          <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827' }}>
            {formatPayDate(payPeriod.start)} — {formatPayDate(payPeriod.end)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Pay Date</div>
          <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#059669' }}>{formatPayDate(payPeriod.payDate)}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.35rem' }}>
          <span>Period progress</span><span>{pct}%</span>
        </div>
        <div style={{ height: '6px', background: '#e0e7ff', borderRadius: '999px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #22c55e, #0f4d2e)', borderRadius: '999px', transition: 'width 0.6s ease' }} />
        </div>
      </div>

      {/* Hours summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div style={{ background: 'white', borderRadius: '10px', padding: '0.75rem', border: '1px solid #e0e7ff', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#4f46e5' }}>{totalHours.toFixed(1)}</div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Hours Logged</div>
        </div>
        <div style={{ background: 'white', borderRadius: '10px', padding: '0.75rem', border: '1px solid #d1fae5', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#059669' }}>{approvedHours.toFixed(1)}</div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Approved</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function EmployeeDashboard() {
  const [entries, setEntries]       = useState([]);
  const [schedule, setSchedule]     = useState([]);
  const [payPeriod, setPayPeriod]   = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const { features } = useOrgSettings();
  const clockEnabled = features.clockInOut;

  const user = getUser();
  const firstName = user?.firstName || 'there';

  // Fetch this employee's time entries
  const fetchEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await fetch('/api/timeEntries?mine=true', { headers: getAuthHeaders() });
      if (res.ok) setEntries(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingEntries(false); }
  }, []);

  // Fetch schedule — /api/schedules/mine returns { "YYYY-MM-DD": [shifts] }
  // Transform into flat array with actual date strings for the UpcomingSchedule widget
  const fetchSchedule = useCallback(async () => {
    setLoadingSchedule(true);
    try {
      const res = await fetch('/api/schedules/mine', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json(); // { "2026-04-27": [{day,startTime,endTime,...}], ... }
        const DAY_OFFSET = { Monday:0, Tuesday:1, Wednesday:2, Thursday:3, Friday:4, Saturday:5, Sunday:6 };
        const flat = [];
        for (const [weekStart, shifts] of Object.entries(data)) {
          const monday = new Date(weekStart + 'T00:00:00');
          for (const shift of shifts) {
            const offset = DAY_OFFSET[shift.day] ?? 0;
            const d = new Date(monday);
            d.setDate(monday.getDate() + offset);
            flat.push({ ...shift, date: d.toISOString().split('T')[0] });
          }
        }
        setSchedule(flat);
      }
    } catch (e) {
      setSchedule([]);
    } finally { setLoadingSchedule(false); }
  }, []);

  // Fetch pay period
  useEffect(() => {
    fetchPayPeriodConfig().then(config => {
      if (config) setPayPeriod(getCurrentPayPeriod(config));
    });
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries, refreshKey]);
  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Stats
  const today = new Date().toDateString();
  const todayEntry = entries.find(e => new Date(e.date).toDateString() === today);
  const pendingCount = entries.filter(e => e.status === 'pending').length;
  const approvedCount = entries.filter(e => e.status === 'approved').length;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fc',
      fontFamily: "'Georgia', 'Times New Roman', serif",
    }}>
      {/* Top header bar */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4c1d95 100%)',
        padding: '1.5rem 2rem',
        color: 'white',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: 0.65, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>ClockEarnings</div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', letterSpacing: '-0.01em' }}>
              {getGreeting()}, {firstName}
            </h1>
            <p style={{ margin: '0.2rem 0 0', opacity: 0.7, fontSize: '0.875rem' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          {/* Quick stats pill row */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {[
              { label: 'Pending', value: pendingCount, color: '#fbbf24' },
              { label: 'Approved', value: approvedCount, color: '#34d399' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', padding: '0.5rem 1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.25rem', fontWeight: '800', color }}>{value}</div>
                <div style={{ fontSize: '0.65rem', opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Clock widget — only shown if org has clockInOut feature */}
            <div style={{ gridColumn: 'span 1' }}>
              {clockEnabled && <ClockWidget onClockOut={() => setRefreshKey(k => k + 1)} />}
            </div>

            {/* Pay period */}
            <PayPeriodCard payPeriod={payPeriod} entries={entries} />
          </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Schedule — only shown if org has scheduling feature */}
            {features.scheduling && (
              <div style={{ background: 'white', borderRadius: '14px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IcoCalendar size={14} color="currentColor"/> <span>This Week</span>
                </h3>
                <UpcomingSchedule schedule={schedule} loading={loadingSchedule} />
              </div>
            )}
          </div>
        </div>

        {/* Recent entries — full width below */}
        <div style={{ marginTop: '1.5rem', background: 'white', borderRadius: '14px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <IcoClock size={14} color="currentColor"/> <span>Recent Time Entries</span>
            </h3>
            <span style={{ fontSize: '0.775rem', color: '#9ca3af' }}>{entries.length} total</span>
          </div>
          <RecentEntries entries={entries} loading={loadingEntries} />
        </div>
      </div>
    </div>
  );
}